import MaterialTable, { MTablePagination } from '@material-table/core'
import { ExportCsv, ExportPdf } from '@material-table/exporters'
import { Circle } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import dateUtils from '../../_helpers/dateUtils'
import schemaUtils from '../../_helpers/schemaUtils'
import stringUtils from '../../_helpers/stringUtils'
import translateUtils from '../../_helpers/translateUtils'
import Guard from '../Guards/Guard'
import AbstractButton from './AbstractButton'
import ImageRender from './Common/Image'
import VideoRender from './Common/Video'
import Boolean from './TableColumns/Boolean'
import DateTime from './TableColumns/DateTime'
import Image from './TableColumns/Image'
import Message from './TableColumns/Message'
import Status from './TableColumns/Status'

const AbstractLayout = function (props) {
  const checkIsToBeShow = (key, value, notToShow) => {
    return (
      !(
        key.toLowerCase().endsWith('id') ||
        key.toLowerCase().endsWith('slug') ||
        key.toLowerCase().endsWith('code') ||
        key === 'count' ||
        key === 'pages' ||
        key === 'page' ||
        key === 'tableData' ||
        props.notToShow?.includes(key) ||
        notToShow?.includes(key) ||
        value === null ||
        value === undefined ||
        value === ''
      ) || props.toShow?.includes(key)
    )
  }
  const getType = (data) =>
    typeof data === 'object'
      ? Array.isArray(data)
        ? 'array'
        : 'object'
      : 'nonObject'
  const switchCasesOnArrayKeysRender = (key) => {
    // status renders as a colored lifecycle chip on every list (its schema
    // type is 'select', which the type switch below has no case for).
    if (key === 'status') return Status(key)
    const type = schemaUtils.getKeyType(key)
    switch (type) {
      case 'date':
      case 'date-time':
      case 'dateTime':
        return DateTime(key)
      case 'boolean':
        return Boolean(key)
      case 'string':
        return Message(key)
      case 'file':
        return Image(key)
      default:
        return null
    }
  }
  const switchCasesOnObjectKeysRender = (key, value) => {
    const type = schemaUtils.getKeyType(key)
    switch (type) {
      case 'date':
        return dateUtils.formatDate(value)
      case 'date-time':
      case 'dateTime':
        return dateUtils.formatDateTime(value)
      case 'time':
        return dateUtils.formatTime(value)
      case 'boolean':
        return stringUtils.boolean(value)
      case 'file':
        return <ImageRender data={value} />
      case 'stringMultiple':
      case 'content':
      case 'contentAr':
        if (value.length === 11)
          return (
            <VideoRender data={`https://www.youtube.com/watch?v=${value}`} />
          )
        return <p dangerouslySetInnerHTML={{ __html: value }} />
      default:
        return /\r|\n/.exec(value)
          ? value.split('\n').map((v, i) => <p key={i}>{v}</p>)
          : value
    }
  }
  const switchCasesOnActions = (name) => {
    switch (name) {
      case 'Contacts':
        return [
          (rowData) => ({
            icon: () => (
              <Circle style={{ color: rowData.contacted ? 'black' : 'red' }} />
            ),
          }),
        ]
      default:
        return null
    }
  }
  const switchCasesOnNavigation = (name, rowData) => {
    switch (name) {
      default:
        return props.navigate
          ? props.navigate.reduce((str, el) => {
              str += `/${rowData[el] ? encodeURIComponent(rowData[el]) : el}`
              return str
            }, '')
          : rowData.code
          ? encodeURIComponent(rowData.code)
          : rowData.email || rowData.slug || rowData._id || null
    }
  }
  const setArrayId = (value) => {
    if (value) {
      value.forEach((v) => {
        if (v._id) v.id = v._id
      })
      return value
    }
    return []
  }
  const getArrayColumns = (value) => {
    const newColumns = []
    if (!value.length) return newColumns
    Object.keys(value[0]).forEach((el) => {
      if (
        Array.isArray(value[0][el]) ||
        !checkIsToBeShow(el, value[0][el]) ||
        !checkIsToBeShow(el, value[0][el], props.arrayNotToShow)
      )
        return
      const column = {
        title: stringUtils.camelCase(el),
        field: el,
      }
      const render = switchCasesOnArrayKeysRender(el)
      // const cellStyle = switchCasesOnArrayKeysStyle(el)
      if (render) column.render = render
      // if (cellStyle) column.cellStyle = cellStyle
      newColumns.push(column)
    })
    return newColumns
  }
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (props.many) {
      if (searchParams.toString() === '')
        props.setQueryParams(props.defaultQueryParams)
      if (searchParams.get('query') === props.title) {
        const searchParamsObject = Object.fromEntries([...searchParams])
        delete searchParamsObject.query
        props.setQueryParams((s) => ({ ...s, ...searchParamsObject }))
      }
    }
  }, [searchParams])
  const [columns, setColumns] = useState([])
  useEffect(() => {
    if (props.many && !props.getQuery.isLoading && !columns.length)
      setColumns(props.getQuery.data.documents)
  }, [props.getQuery.isLoading, props.getQuery.data?.count])
  if (props.getQuery.isLoading && !props.many)
    return (
      <span className="spanText">{translateUtils.translate('Loading...')}</span>
    )
  if (props.getQuery.isError) {
    enqueueSnackbar(
      `${translateUtils.translate('Server')} ${translateUtils.translate(
        'Error'
      )}: ${props.getQuery.error.response.data.message}`,
      { variant: 'error', TransitionProps: { direction: 'left' } }
    )
    return (
      <span className="spanText">
        {`${translateUtils.translate('Error')}: ${
          props.getQuery.error.response.data.message
        }`}
      </span>
    )
  }
  const nonObjects = []
  const objects = []
  const arrays = []
  if (props.many && props.getQuery.isLoading) arrays.push('documents')
  if (props.getQuery.data)
    Object.keys(props.getQuery.data).forEach((el) => {
      if (!checkIsToBeShow(el, props.getQuery.data[el])) return null
      const type = getType(props.getQuery.data[el])
      if (type === 'nonObject') nonObjects.push(el)
      if (type === 'object' && Object.keys(props.getQuery.data[el]).length)
        objects.push(el)
      if (type === 'array') arrays.push(el)
    })
  const getDataToShow = (key, value, type) => {
    if (!checkIsToBeShow(key, value)) return null
    switch (type) {
      case 'object':
        return (
          <>
            <div className="fullWidth center">
              <h2>
                <strong>{stringUtils.camelCase(key)}</strong>
              </h2>
            </div>
            <div className="fullWidth singleContainer">
              {Object.keys(value).map((el) => getDataToShow(el, value[el]))}
            </div>
          </>
        )
      case 'array':
        return typeof value[0] === 'object' ? (
          <>
            <div className="fullWidth center">
              <h2>
                <strong>
                  {stringUtils.camelCase(props.many ? props.title : key)}
                </strong>
              </h2>
            </div>
            <Guard condition={props.many && props.tableActions?.length}>
              <div className="collectionActions btnActions">
                {props.tableActions?.map((action) => (
                  <AbstractButton
                    key={action.buttonLabel}
                    buttonLabel={action.buttonLabel}
                    buttonColor={action.buttonColor}
                    form={action.form}
                    onSubmit={action.onSubmit}
                    body={action.body}
                    setBody={action.setBody}
                    confirm={action.confirm}
                    info={action.info}
                    withReset={action.withReset}
                    resetFunction={action.resetFunction}
                    withClear={action.withClear}
                    clearFunction={action.clearFunction}
                    withAction={action.withAction}
                  />
                ))}
              </div>
            </Guard>
            <div className="tableComponent">
              <MaterialTable
                title={stringUtils.camelCase(props.many ? props.title : key)}
                data={setArrayId(
                  props.many ? props.getQuery.data?.documents : value
                )}
                columns={getArrayColumns(props.many ? columns : value)}
                options={{
                  exportMenu: [
                    {
                      label: translateUtils.translate('Export PDF'),
                      exportFunc: (cols, data) =>
                        ExportPdf(
                          cols,
                          data,
                          stringUtils.camelCase(props.many ? props.title : key)
                        ),
                    },
                    {
                      label: translateUtils.translate('Export CSV'),
                      exportFunc: (cols, data) =>
                        ExportCsv(
                          cols,
                          data,
                          stringUtils.camelCase(props.many ? props.title : key)
                        ),
                    },
                  ],
                  searchText:
                    Object.fromEntries([...searchParams])?.search || '',
                  searchDebounceDelay: 1000,
                  tableWidth: 'full',
                  padding: 'dense',
                  actionsColumnIndex: -1,
                  columnsButton: true,
                  maxBodyHeight: '50vh',
                  headerStyle: { textAlign: 'center' },
                  pageSize: 10,
                }}
                actions={
                  switchCasesOnActions(props.many ? props.title : key) || []
                }
                onRowClick={(event, rowData) => {
                  const navigation = switchCasesOnNavigation(key, rowData)
                  if (navigation) navigate(navigation)
                }}
                style={{ overflowX: 'hidden' }}
                {...(props.many && {
                  isLoading: props.getQuery.isLoading,
                  onSearchChange: (search) =>
                    setSearchParams({
                      ...Object.fromEntries([...searchParams]),
                      search,
                      page: 1,
                      query: props.title,
                    }),
                  onOrderChange: (index, order) =>
                    setSearchParams({
                      ...Object.fromEntries([...searchParams]),
                      sort: `${order === 'desc' ? '-' : ''}${
                        getArrayColumns(columns)[index]?.field || ''
                      }`,
                      page: 1,
                      query: props.title,
                    }),
                  components: {
                    Pagination: () => (
                      <MTablePagination
                        rowsPerPage={10}
                        page={
                          (props.getQuery.data?.page ||
                            Object.fromEntries([...searchParams])?.page ||
                            1) - 1
                        }
                        count={props.getQuery.data?.count}
                        onPageChange={(_, page) =>
                          setSearchParams({
                            ...Object.fromEntries([...searchParams]),
                            page: page + 1,
                            query: props.title,
                          })
                        }
                      />
                    ),
                  },
                })}
              />
            </div>
          </>
        ) : typeof value[0] === 'string' || typeof value[0] === 'number' ? (
          <>
            <div className="fullWidth center">
              <h2>
                <strong>{stringUtils.camelCase(key)}</strong>
              </h2>
            </div>
            <div className="fullWidth singleContainer">
              {value.map((v) => (
                <div className="singleData">
                  {switchCasesOnObjectKeysRender(key, v)}
                </div>
              ))}
            </div>
          </>
        ) : null
      default:
        return (
          <div key={key + value + type} className="singleData">
            <strong>{stringUtils.camelCase(key)}</strong>
            {switchCasesOnObjectKeysRender(key, value)}
          </div>
        )
    }
  }
  return (
    <div className="singleComponent">
      <Guard condition={props.actions?.length}>
        <div className="btnActions">
          {props.actions?.map((action) => (
            <AbstractButton
              key={action.buttonLabel}
              buttonLabel={action.buttonLabel}
              buttonColor={action.buttonColor}
              form={action.form}
              onSubmit={action.onSubmit}
              body={action.body}
              setBody={action.setBody}
              confirm={action.confirm}
              info={action.info}
              withReset={action.withReset}
              resetFunction={action.resetFunction}
              withClear={action.withClear}
              clearFunction={action.clearFunction}
              withAction={action.withAction}
            />
          ))}
        </div>
      </Guard>
      <Guard condition={nonObjects.length}>
        <div className="fullWidth center">
          <h2>
            {translateUtils.translate(props.title)}{' '}
            {props.many && translateUtils.translate('Information')}
          </h2>
        </div>
        <div className="fullWidth singleContainer">
          {nonObjects.map((el) =>
            getDataToShow(el, props.getQuery.data[el], 'nonObject')
          )}
        </div>
      </Guard>
      <Guard condition={objects.length}>
        {objects.map((el) => {
          if (
            Object.values(props.getQuery.data[el]).reduce(
              (flag, value) => !!value || flag,
              false
            )
          )
            return getDataToShow(el, props.getQuery.data[el], 'object')
          return null
        })}
      </Guard>
      <Guard condition={arrays.length}>
        {arrays.map((el) =>
          getDataToShow(
            el,
            props.getQuery.data?.[el].length
              ? props.getQuery.data[el]
              : props.many
              ? [{}]
              : [],
            'array'
          )
        )}
      </Guard>
    </div>
  )
}
export default AbstractLayout
